/**
 * Utilitaire pour convertir les données de refoulement en RefoulementTicketData
 */

import { RefoulementTicketData } from '@/types/refoulementTicket';
import { RefoulementResponse } from '@/services/refoulementService';
import { Pesée } from '@/types/weighing';

/**
 * Convertit une réponse de refoulement et une pesée en RefoulementTicketData
 */
export const convertToRefoulementTicketData = (
  refoulement: RefoulementResponse,
  pesee: Pesée | any,
  additionalData?: {
    siteInfo?: { nom: string; id: string } | null;
    tenantInfo?: { nom: string; id: string } | null;
    pontBasculeInfo?: { nom: string; site: string; tenant: string } | null;
  }
): RefoulementTicketData => {
  const siteInfo = additionalData?.siteInfo;
  const tenantInfo = additionalData?.tenantInfo;
  const pontBasculeInfo = additionalData?.pontBasculeInfo;

  // Extraire les informations de la pesée
  const getPeseeValue = (field: any, fallback: any = '') => {
    if (typeof field === 'string') return field;
    if (typeof field === 'object' && field !== null) {
      return field.nom || field.immatriculation || field.username || field.name || fallback;
    }
    return fallback;
  };

  const poidsBrut = pesee.poids_brut ? parseFloat(pesee.poids_brut.toString()) : 0;
  const poidsTare = pesee.poids_tare ? parseFloat(pesee.poids_tare.toString()) : 0;
  const poidsSacs = pesee.poids_acceptes ? parseFloat(pesee.poids_acceptes.toString()) : 0;
  const poidsNet = pesee.poids_net 
    ? parseFloat(pesee.poids_net.toString())
    : (poidsBrut - (poidsTare + poidsSacs));

  // Normaliser les motifs pour s'assurer qu'ils ont toutes les propriétés nécessaires
  const normalizedMotifs = (refoulement.motifs || []).map((motif: any) => {
    // Remplacer les underscores par des espaces dans le libellé
    const cleanLibelle = (str: string) => {
      if (!str) return '';
      return str.replace(/_/g, ' ');
    };
    
    // Si le motif est déjà un objet complet avec toutes les propriétés
    if (motif.libelle || motif.nom) {
      const libelleRaw = motif.libelle || motif.nom || String(motif.id || motif || '');
      const criticiteRaw = motif.criticite || motif.criticité || '';
      const normalized = {
        libelle: cleanLibelle(libelleRaw),
        nom: cleanLibelle(motif.nom || motif.libelle || String(motif.id || motif || '')),
        description: motif.description || motif.desc || '',
        categorie: motif.categorie || motif.category || '',
        criticite: criticiteRaw && criticiteRaw !== 'Non défini' ? criticiteRaw : '',
        code: motif.code || motif.id || ''
      };
      return normalized;
    }
    // Si le motif est juste un ID ou un objet minimal, essayer de récupérer les infos
    const libelleRaw = motif.libelle || motif.nom || motif.name || String(motif.id || motif || '');
    const criticiteRaw = motif.criticite || motif.criticité || '';
    const normalized = {
      libelle: cleanLibelle(libelleRaw),
      nom: cleanLibelle(motif.nom || motif.libelle || motif.name || String(motif.id || motif || '')),
      description: motif.description || motif.desc || '',
      categorie: motif.categorie || motif.category || '',
      criticite: criticiteRaw && criticiteRaw !== 'Non défini' ? criticiteRaw : '',
      code: motif.code || motif.id || motif || ''
    };
    return normalized;
  });

  const ticketData: RefoulementTicketData = {
    numero_refoulement: refoulement.numero_refoulement,
    date_refoulement: refoulement.date_refoulement,
    agent_refoulement: refoulement.agent_refoulement_nom || 
                       refoulement.agent_refoulement_username || 
                       refoulement.agent_refoulement?.toString() || 
                       'Agent non spécifié',
    agent_refoulement_nom: refoulement.agent_refoulement_nom,
    agent_refoulement_username: refoulement.agent_refoulement_username,
    observations: refoulement.observations,
    motifs: normalizedMotifs,
    pesee: {
      reference: pesee.reference || pesee.code || '',
      code: pesee.code || pesee.reference || '',
      grossWeight: poidsBrut,
      tareWeight: poidsTare,
      netWeight: poidsNet,
      vehicle: getPeseeValue(
        pesee.camion_info?.immatriculation || pesee.camion,
        'Non spécifié'
      ),
      driver: getPeseeValue(
        pesee.chauffeur_info?.nom || pesee.chauffeur,
        'Non spécifié'
      ),
      chauffeurCode: pesee.chauffeur_info?.code || 
                     pesee.chauffeur?.code || 
                     pesee.chauffeur_info?.permis || 
                     pesee.chauffeur?.permis || 
                     '',
      product: getPeseeValue(
        pesee.produit_info?.nom || pesee.produit,
        'Non spécifié'
      ),
      site: siteInfo?.nom || 
            pontBasculeInfo?.site || 
            getPeseeValue(pesee.pont_bascule, 'Site non spécifié'),
      tenant: tenantInfo?.nom || 
              pontBasculeInfo?.tenant || 
              'Tenant non spécifié',
      grossAt: pesee.date_heure_premiere_pesee || pesee.heure_entree || pesee.date_heure || pesee.created_at || new Date().toISOString(),
      tareAt: pesee.date_heure_deuxieme_pesee || pesee.heure_sortie || pesee.updated_at || pesee.created_at || new Date().toISOString(),
      grossUser: getPeseeValue(
        pesee.agent_premiere_pesee_nom || pesee.agent_premiere_pesee,
        'Agent non spécifié'
      ),
      tareUser: getPeseeValue(
        pesee.agent_deuxieme_pesee_nom || pesee.agent_deuxieme_pesee,
        'Agent non spécifié'
      ),
      analysisUser: getPeseeValue(
        pesee.agent_analyse_qualite_nom || pesee.agent_analyse_qualite,
        'Agent non spécifié'
      ),
      provenance: pesee.provenance || '',
      destination: pesee.destination || '',
      transporterName: getPeseeValue(pesee.transporteur, ''),
      trailerRegistration: getPeseeValue(pesee.remorque, ''),
      supplierIdentity: getPeseeValue(pesee.fournisseur, ''),
      supplierCode: pesee.fournisseur_info?.code_supplier || 
                    pesee.fournisseur?.code_supplier || 
                    '',
      clientCode: getPeseeValue(pesee.client, ''),
      clientCodeValue: pesee.client_info?.code_client || 
                       pesee.client?.code_client || 
                       '',
      exporterCode: getPeseeValue(pesee.exportateur, ''),
      productNature: pesee.produit_info?.nature || '',
      productOrigin: pesee.produit_info?.origine || '',
      bushLotNumber: pesee.bush_lot_number || '',
      transferSheetNumber: pesee.transfer_sheet_number || '',
      stickerNumber: pesee.sticker_number || '',
      bagWeight: poidsSacs,
      sacsCount: pesee.sacs_count ? {
        nb_sacs_declares: pesee.sacs_count.nb_sacs_declares,
        nb_sacs_sains: pesee.sacs_count.nb_sacs_sains,
        nb_sacs_recond: pesee.sacs_count.nb_sacs_recond,
        nb_sacs_humides: pesee.sacs_count.nb_sacs_humides,
        nb_sacs_dechires: pesee.sacs_count.nb_sacs_dechires,
        nb_sacs_rejetes: pesee.sacs_count.nb_sacs_rejetes,
        nb_sacs_acceptes: pesee.sacs_count.nb_sacs_acceptes
      } : undefined,
      analyse_qualite: pesee.analyse_qualite ? {
        grainnage: pesee.analyse_qualite.grainnage,
        humidite: pesee.analyse_qualite.humidite,
        quality: pesee.analyse_qualite.quality,
        quality_level: pesee.analyse_qualite.quality_level,
        quality_decision: pesee.analyse_qualite.quality_decision,
        observations: pesee.analyse_qualite.observations
      } : undefined
    },
    generated_at: new Date().toISOString(),
    campaignYear: new Date().getFullYear().toString()
  };

  return ticketData;
};
